import { APP_SCORING_FORMAT, APP_SCORING_MAX_SCORE } from '@/constants/config';
import { buildStyles } from 'react-circular-progressbar';
import { getScoreColorScale } from './get-score-color-scale';
import { getSkillLevel } from '@/domains';
import { normalizeScoreTo } from './normalize-score-to';
import { SCORE_CIRCLE_DEFAULTS } from '../config';
import { SKILL_LEVELS } from '@/constants/labels';
import { useMemo } from 'react';
import type { ScoreCircleProps, TScoreCircleVariant } from '../types';

// ================================
// Types
// ================================

// Keep this hook focused on the subset of ScoreCircle props that affect
// computed rendering state. Visual text concerns (printing, animated number
// formatting, etc.) stay in the component layer.
type UseScoreCircleParams = Pick<
  ScoreCircleProps,
  | 'circleRatio'
  | 'isLoading'
  | 'passingThreshold'
  | 'rotation'
  | 'runtimeRatingCutoffs'
  | 'simulationType'
  | 'useRolePlayGradient'
  | 'value'
  | 'valueMax'
>;

/**
 * Encapsulates ScoreCircle's derived variant, coloring, style, and geometry values.
 *
 * This hook is intentionally "calculation only":
 * - it derives display mode and color decisions
 * - it builds circular-progress style objects
 * - it computes repeated geometry constants used by the component tree
 *
 * It does not render JSX and it does not manage animation timing.
 */
export const useScoreCircle = ({
  // Mirror ScoreCircle defaults from a single shared source of truth.
  circleRatio = SCORE_CIRCLE_DEFAULTS.circleRatio,
  isLoading = false,
  passingThreshold = SCORE_CIRCLE_DEFAULTS.passingThreshold,
  rotation = SCORE_CIRCLE_DEFAULTS.rotation,
  runtimeRatingCutoffs,
  simulationType = SCORE_CIRCLE_DEFAULTS.simulationType,
  useRolePlayGradient = SCORE_CIRCLE_DEFAULTS.useRolePlayGradient,
  value = SCORE_CIRCLE_DEFAULTS.value,
  valueMax = SCORE_CIRCLE_DEFAULTS.valueMax,
}: UseScoreCircleParams) => {
  // Palette tokens for role-play band colors (L1-L4).
  const c = getScoreColorScale();

  // ================================
  // Variant Derivation
  // ================================
  // Variant drives all rendering decisions in ScoreCircle, so we derive it first.
  const variant = useMemo<TScoreCircleVariant>(() => {
    if (simulationType === 'role-play') {
      return 'role-play';
    }

    return 'pass/fail';
  }, [simulationType]);

  /**
   * Score normalization is necessary to align the input `value` with the expected
   * ranges of the score circle geometry and coloring logic. In percentage scoring
   * environments, we convert raw scores into percentage form so that the color
   * bands and passing threshold align correctly. In raw scoring environments, we
   * assume the input value is already in the correct form.
   */
  const normalizedValue = useMemo(() => {
    if (APP_SCORING_FORMAT === 'percentage') {
      // If value is already in percentage form (1-100), return as is
      if (value >= 1 && value <= 100) {
        return value;
      }

      const rounded = Math.round(value * 100);
      const hasLongDecimals = value.toString().split('.')[1]?.length > 10;

      if (hasLongDecimals) {
        return Math.round(rounded * 100) / 100;
      }

      return rounded === 100 ? 100 : rounded;
    }

    return value;
  }, [value]);

  /**
   * Normalize valueMax in percentage scoring environments to ensure that the score circle's
   * geometry and coloring logic align correctly. In percentage scoring mode, the maximum value
   * should be 100 to represent 100%. In raw scoring mode, we assume valueMax is already correct.
   */
  const normalizedValueMax = useMemo(() => {
    if (APP_SCORING_FORMAT === 'percentage') {
      return APP_SCORING_MAX_SCORE;
    }

    return valueMax;
  }, [valueMax]);

  // ================================
  // Skill Level Derivation
  // ================================
  // Skill level only matters in role-play mode when role-play gradient is
  // disabled and a single color is chosen by skill bucket.
  const skillLevel = useMemo(() => {
    let valueForSkillLevel = 0;

    if (variant !== 'role-play') return null;

    // `getSkillLevel` expects ratio form in percentage-scoring environments.
    if (APP_SCORING_FORMAT === 'percentage') {
      valueForSkillLevel = normalizeScoreTo(value, 'ratio');
    } else {
      valueForSkillLevel = value;
    }

    return getSkillLevel(valueForSkillLevel, runtimeRatingCutoffs);
  }, [runtimeRatingCutoffs, value, variant]);

  // ================================
  // Path Color Selection
  // ================================
  // Role-play has two color modes:
  // 1) gradient overlay enabled: base path should be transparent
  // 2) gradient overlay disabled: choose a solid color by skill level
  //
  // Pass/fail uses threshold coloring:
  // - green when value >= passingThreshold
  // - yellow otherwise
  const colors = useMemo(() => {
    if (isLoading) {
      return {
        pathColor: 'var(--color-neutral-gray-300)',
      }
    }
    
    if (variant === 'role-play') {
      if (useRolePlayGradient) {
        return {
          pathColor: 'transparent',
        };
      }

      switch (skillLevel) {
        case SKILL_LEVELS.EXPERT:
          return { pathColor: c[4] };
        case SKILL_LEVELS.ADVANCED:
          return { pathColor: c[3] };
        case SKILL_LEVELS.SKILLED:
        case SKILL_LEVELS.INTERMEDIATE:
          return { pathColor: c[2] };
        case SKILL_LEVELS.AWARENESS:
        case SKILL_LEVELS.BEGINNER:
        case SKILL_LEVELS.NOVICE:
        default:
          return { pathColor: c[1] };
      }
    }

    if (normalizedValue >= passingThreshold) {
      return {
        pathColor: 'var(--color-accent-green-100)',
      };
    }

    return {
      pathColor: 'var(--color-accent-yellow-100)',
    };
  }, [isLoading, normalizedValue, passingThreshold, skillLevel, useRolePlayGradient, variant, c]);

  // True only when the pass/fail arc endpoint aligns with the passing tick.
  // We intentionally use an epsilon check so floating-point inputs still behave
  // correctly around the threshold boundary.
  const atPassingThreshold =
    variant === 'pass/fail' &&
    Math.abs(normalizedValue - passingThreshold) < 0.0001;

  // ================================
  // CircularProgressbar Style Construction
  // ================================
  // Build style config once from derived values and keep dependency list tight.
  const styles = useMemo(() => {
    // When atPassingThreshold, use `butt` so the path meets the passing-line
    // tick cleanly. The trail remains `round` so the lower trail corners stay
    // visually rounded regardless of path cap mode.
    const built = buildStyles({
      pathTransition: 'none',
      rotation,
      strokeLinecap: atPassingThreshold ? 'butt' : 'round',
      trailColor: '#d9d9d9',
      pathColor: colors.pathColor,
    });

    return {
      ...built,
      trail: { ...built.trail, strokeLinecap: 'round' as const },
    };
  }, [atPassingThreshold, colors, rotation]);

  // ================================
  // Geometry & Layout Constants
  // ================================
  // These constants are consumed by ScoreCircle render and subcomponents.
  // Grouping them here keeps render JSX lean and keeps geometry math colocated.
  const numSeparators = 100;
  const numSeparatorsForPassingLine = 10;
  const passingLineHeight = 33.614;
  const passingLineIndex = 8;
  const passingLineMarginTop = -21;

  // Convert passing line index into turn-space so it rotates with the same
  // arc system used by CircularProgressbar.
  const passingLineTurn = (circleRatio / numSeparatorsForPassingLine) * passingLineIndex + rotation;

  // Stroke width is intentionally centralized to keep overlay geometry in sync.
  const strokeWidth = 5;

  // Public contract consumed by ScoreCircle.
  return {
    atPassingThreshold,
    colors,
    normalizedValue,
    normalizedValueMax,
    numSeparators,
    numSeparatorsForPassingLine,
    passingLineHeight,
    passingLineMarginTop,
    passingLineTurn,
    skillLevel,
    strokeWidth,
    styles,
    variant,
  };
};

export default useScoreCircle;
